import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../api/api';
import Icon from '../components/Icon';
import { EditableList, EditableText } from '../components/Editable';
import { useProducts } from '../product/ProductContext';
import {
    CREATE_KINDS, KIND_BAR_COLORS, avatarColors, initials, kindForContentType, shortName,
} from '../product/productUtils';

const CONTENT_TYPES = Object.values(CREATE_KINDS).map(kind => kind.contentType);

// Average clicks per tracked link for this ICP, per content type, against the product's average
const whatWorks = (icp, links) => {
    const typed = links.filter(link => link.contentType);
    const average = (list) => (list.length ? list.reduce((sum, link) => sum + (link.clicks || 0), 0) / list.length : 0);
    const overall = average(typed);
    const own = links.filter(link => link.icpId === icp.id);
    const rows = Object.keys(CREATE_KINDS)
        .map(kind => {
            const group = own.filter(link => link.contentType && kindForContentType(link.contentType) === kind);
            if (!group.length) return null;
            return { kind, label: CREATE_KINDS[kind].label, posts: group.length, ratio: overall ? average(group) / overall : 0 };
        })
        .filter(Boolean)
        .sort((a, b) => b.ratio - a.ratio);
    const best = own.filter(link => link.hook).sort((a, b) => (b.clicks || 0) - (a.clicks || 0))[0];
    return { rows, posts: own.length, best, maxRatio: Math.max(...rows.map(row => row.ratio), 1) };
};

const NewIcp = ({ productId, onCreated, onCancel }) => {
    const [describe, setDescribe] = useState('');
    const [isWorking, setIsWorking] = useState(false);
    const [error, setError] = useState('');

    const create = async (useAi) => {
        setIsWorking(true);
        setError('');
        try {
            let draft = { name: 'New ICP', descriptor: describe.trim(), pains: [], goals: [], objections: [], vocabulary: [], channels: [], contentTypes: [] };
            if (useAi) {
                const [generated] = await api.generateIcps(productId, { describe: describe.trim() });
                if (!generated) throw new Error('No ICP drafted');
                draft = generated;
            }
            onCreated(await api.createIcp(productId, { ...draft, isPrimary: false }));
        } catch (e) {
            setError(useAi ? "Couldn't draft the ICP. Try again or start blank." : "Couldn't create the ICP.");
            setIsWorking(false);
        }
    };

    return (
        <div className="ls-card" style={{ maxWidth: 640, gap: 14 }}>
            <div style={{ fontSize: 19, fontWeight: 700 }}>New ICP</div>
            <div style={{ fontSize: 14, color: 'var(--secondary)' }}>
                Describe who they are in a sentence or two. The AI fills in pains, goals, objections and the words they use from your product profile.
            </div>
            <textarea
                className="ls-textarea"
                rows={3}
                autoFocus
                value={describe}
                onChange={(e) => setDescribe(e.target.value)}
                placeholder="e.g. Parents of toddlers who cook one meal for the whole family"
            />
            {error && <div style={{ fontSize: 13, color: 'var(--danger-text)' }}>{error}</div>}
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <button type="button" className="ls-btn ls-btn-primary" disabled={isWorking || !describe.trim()} onClick={() => create(true)}>
                    {isWorking ? <span className="ls-spinner" style={{ borderTopColor: 'var(--accent-ink)', borderColor: 'rgba(5, 48, 44, .25)' }} /> : <Icon name="auto_awesome" size={18} />}
                    {isWorking ? 'Drafting…' : 'Draft with AI'}
                </button>
                <button type="button" className="ls-btn ls-btn-secondary" disabled={isWorking} onClick={() => create(false)}>Start blank</button>
                <button type="button" className="ls-btn ls-btn-ghost" onClick={onCancel}>Cancel</button>
            </div>
        </div>
    );
};

const IcpsScreen = () => {
    const navigate = useNavigate();
    const { icpId } = useParams();
    const { currentProduct: product, icps, setIcps, loadIcps } = useProducts();
    const [links, setLinks] = useState([]);
    const [isCreating, setIsCreating] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        api.listTrackedLinks().then(setLinks).catch(() => setLinks([]));
    }, []);

    const selectedIndex = Math.max(0, icps.findIndex(icp => icp.id === icpId));
    const icp = isCreating ? null : icps[selectedIndex];

    const clickShare = useMemo(() => {
        const byIcp = links.reduce((acc, link) => {
            if (link.icpId) acc[link.icpId] = (acc[link.icpId] || 0) + (link.clicks || 0);
            return acc;
        }, {});
        const total = Object.values(byIcp).reduce((sum, value) => sum + value, 0);
        return (id) => (total ? Math.round(((byIcp[id] || 0) / total) * 100) : null);
    }, [links]);

    const works = useMemo(() => (icp ? whatWorks(icp, links) : null), [icp, links]);

    const save = async (changes) => {
        setError('');
        try {
            const updated = await api.updateIcp(product.id, icp.id, changes);
            // Making one primary un-primaries the others on the server
            if (changes.isPrimary) {
                await loadIcps();
            } else {
                setIcps(prev => prev.map(item => (item.id === updated.id ? updated : item)));
            }
        } catch (e) {
            setError("Couldn't save your change. Please try again.");
        }
    };

    const remove = async () => {
        if (!window.confirm(`Delete ${icp.name}? Tracked links keep their clicks but lose the ICP.`)) return;
        try {
            await api.deleteIcp(product.id, icp.id);
            await loadIcps();
            navigate('/dashboard/icps', { replace: true });
        } catch (e) {
            setError("Couldn't delete the ICP.");
        }
    };

    const handleCreated = async (created) => {
        setIsCreating(false);
        await loadIcps();
        navigate(`/dashboard/icps/${created.id}`);
    };

    const createKind = kindForContentType(icp?.contentTypes?.[0] || '');
    const colors = avatarColors(selectedIndex);
    const pronounLabel = icp ? `Words ${shortName(icp.name)} uses` : '';

    return (
        <div className="ls-icps-layout">
            <div className="ls-icp-list">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 8px 8px' }}>
                    <span style={{ fontSize: 20, fontWeight: 700 }}>ICPs</span>
                    <button type="button" className="ls-link" onClick={() => setIsCreating(true)}>+ New</button>
                </div>
                {icps.map((item, index) => {
                    const share = clickShare(item.id);
                    const itemColors = avatarColors(index);
                    const sub = [item.isPrimary ? 'Primary' : null, share != null ? `${share}% of clicks` : null].filter(Boolean).join(' · ');
                    return (
                        <button
                            type="button"
                            key={item.id}
                            className={`ls-icp-item${!isCreating && index === selectedIndex ? ' is-selected' : ''}`}
                            onClick={() => {
                                setIsCreating(false);
                                navigate(`/dashboard/icps/${item.id}`);
                            }}
                        >
                            <div className="ls-avatar" style={{ width: 36, height: 36, fontSize: 12, background: itemColors.bg, color: itemColors.fg }}>
                                {initials(item.name)}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div className="ls-row-title">{item.name}</div>
                                <div className="ls-row-sub">{sub || item.descriptor}</div>
                            </div>
                        </button>
                    );
                })}
            </div>

            <div className="ls-icp-detail">
                {error && <div className="ls-error" role="alert"><Icon name="error" />{error}</div>}

                {isCreating || icps.length === 0 ? (
                    icps.length === 0 && !isCreating ? (
                        <div className="ls-welcome" style={{ marginTop: '8vh' }}>
                            <h1 className="ls-page-title">Who are you writing for?</h1>
                            <div style={{ fontSize: 16, color: 'var(--secondary)', lineHeight: 1.5 }}>
                                Ideal Customer Profiles are the people every post, script and ad is written for.
                            </div>
                            <button type="button" className="ls-btn ls-btn-primary" onClick={() => setIsCreating(true)}>
                                <Icon name="add" size={18} />Add an ICP
                            </button>
                        </div>
                    ) : (
                        <NewIcp productId={product.id} onCreated={handleCreated} onCancel={() => setIsCreating(false)} />
                    )
                ) : icp && (
                    <>
                        <div className="ls-page-header" style={{ alignItems: 'center' }}>
                            <div style={{ display: 'flex', gap: 14, alignItems: 'center', minWidth: 0 }}>
                                <div className="ls-avatar" style={{ width: 52, height: 52, fontSize: 17, background: colors.bg, color: colors.fg }}>
                                    {initials(icp.name)}
                                </div>
                                <div style={{ minWidth: 0 }}>
                                    <div style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-.02em', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                                        <EditableText value={icp.name} label="Name" onSave={(name) => name && save({ name })} />
                                        {icp.isPrimary && <span className="ls-primary-badge">PRIMARY</span>}
                                    </div>
                                    <div style={{ fontSize: 14, color: 'var(--muted)' }}>
                                        <EditableText value={icp.descriptor} label="Descriptor" placeholder="Who they are, in a few words" onSave={(descriptor) => save({ descriptor })} />
                                    </div>
                                </div>
                            </div>
                            <div className="ls-actions" style={{ alignItems: 'center' }}>
                                {!icp.isPrimary && (
                                    <button type="button" className="ls-link" onClick={() => save({ isPrimary: true })}>Make primary</button>
                                )}
                                <button type="button" className="ls-btn ls-btn-ghost" aria-label={`Delete ${icp.name}`} title="Delete ICP" onClick={remove}>
                                    <Icon name="delete" size={20} />
                                </button>
                                <button
                                    type="button"
                                    className="ls-btn ls-btn-primary"
                                    onClick={() => navigate(`/dashboard/${createKind}?icp=${icp.id}`)}
                                >
                                    Create for {shortName(icp.name)}
                                </button>
                            </div>
                        </div>

                        <div className="ls-grid-2">
                            {[
                                { key: 'pains', label: 'Pains', placeholder: 'Add a pain' },
                                { key: 'goals', label: 'Goals', placeholder: 'Add a goal' },
                                { key: 'objections', label: 'Objections', placeholder: 'Add an objection', quote: true },
                            ].map(section => (
                                <div className="ls-card" key={section.key} style={{ padding: 18, gap: 8 }}>
                                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--muted)' }}>{section.label}</div>
                                    <EditableList
                                        items={icp[section.key] || []}
                                        as="lines"
                                        quote={section.quote}
                                        placeholder={section.placeholder}
                                        label={section.label}
                                        onSave={(items) => save({ [section.key]: items })}
                                    />
                                </div>
                            ))}
                            <div className="ls-card" style={{ padding: 18, gap: 8 }}>
                                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--muted)' }}>{pronounLabel}</div>
                                <EditableList
                                    items={icp.vocabulary || []}
                                    quote
                                    placeholder="A phrase they use"
                                    label="their words"
                                    onSave={(vocabulary) => save({ vocabulary })}
                                />
                            </div>
                            <div className="ls-card" style={{ padding: 18, gap: 8 }}>
                                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--muted)' }}>Where to reach them</div>
                                <EditableList
                                    items={icp.channels || []}
                                    placeholder="e.g. TikTok"
                                    label="channels"
                                    onSave={(channels) => save({ channels })}
                                />
                            </div>
                            <div className="ls-card" style={{ padding: 18, gap: 8 }}>
                                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--muted)' }}>Content that fits</div>
                                <div className="ls-chips">
                                    {CONTENT_TYPES.map(type => {
                                        const on = (icp.contentTypes || []).includes(type);
                                        return (
                                            <button
                                                type="button"
                                                key={type}
                                                className="ls-chip is-medium"
                                                aria-pressed={on}
                                                onClick={() => save({ contentTypes: on ? icp.contentTypes.filter(t => t !== type) : [...(icp.contentTypes || []), type] })}
                                                style={{
                                                    border: 0,
                                                    cursor: 'pointer',
                                                    fontFamily: 'inherit',
                                                    ...(on ? { background: 'var(--ink)', color: '#fff' } : {}),
                                                }}
                                            >
                                                {on && <Icon name="check" size={14} />}{type}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>

                        <div className="ls-card" style={{ padding: 18 }}>
                            <div className="ls-card-header">
                                <span style={{ fontSize: 15, fontWeight: 600 }}>What works for {shortName(icp.name)}</span>
                                {works.posts > 0 && <span style={{ fontSize: 12, color: 'var(--muted)' }}>Learned from {works.posts} post{works.posts === 1 ? '' : 's'}</span>}
                            </div>
                            {works.rows.length === 0 ? (
                                <div className="ls-empty">
                                    Nothing yet. Tracked links made for {shortName(icp.name)} (from Create, the content plan or Tracked links) show here once they get clicks.
                                </div>
                            ) : works.rows.map(row => (
                                <div className="ls-works-row" key={row.kind}>
                                    <span>{row.label}</span>
                                    <div className="ls-works-track" title={`${row.posts} post${row.posts === 1 ? '' : 's'}`}>
                                        <div style={{ width: `${Math.max(4, (row.ratio / works.maxRatio) * 100)}%`, background: KIND_BAR_COLORS[row.kind] }} />
                                    </div>
                                    <span style={{ textAlign: 'right', fontWeight: 600 }}>{row.ratio.toFixed(1)}× avg</span>
                                </div>
                            ))}
                            {works.best && (
                                <div style={{ fontSize: 13, color: 'var(--secondary)', borderTop: '1px solid var(--subtle)', paddingTop: 10 }}>
                                    Best hook so far: “{works.best.hook}”
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default IcpsScreen;
